<template>
  <div>
    <div
      class="position-relative height-211 border--solid border--complementary-50 border-radius-8 cursor--pointer"
      @click.stop="handleOpenViewModal"
    >
      <div
        class="position-relative width-percent-100 height-percent-100 border-radius-8"
      >
        <div v-if="isImage" class="width-percent-100 height-percent-100">
          <b-img-lazy
            class="object-fit--cover width-percent-100 height-percent-100 border-radius-8"
            :src="thumbnail"
            :onerror="`this.src='${defaultImage}';`"
          />
          <div
            class="image-cover-background position-absolute left-0 top-0 width-percent-100 height-percent-100 border-radius-8"
          ></div>
        </div>
        <div
          v-else
          class="background-double-light-gray color-complementary-200 height-139 flex-center"
        >
          <mdicon :name="fileTypeIcon" size="48" />
        </div>

        <DropdownModal
          class="position-absolute top-8 left-8 cursor--pointer"
          :modalWidth="241"
          modalClass="left-0 top-26 box-shadow--modal"
          :hasEventCapturing="false"
        >
          <template #click-area>
            <span
              class="image-cover-background color-surface flex-center width-24 height-24 border-radius-50"
            >
              <mdicon name="dots-vertical" size="16" />
            </span>
          </template>
          <template #content>
            <ul class="margin-0 padding-0 list-style--none">
              <li
                v-for="({ click, icon, caption }, index) in filteredMenuItems"
                :key="`file-box-${index}`"
                class="border-radius-4 padding-x-8 padding-y-4 margin-bottom-16 is-hover flex-start"
                @click.stop="click()"
              >
                <span class="color-complementary-200 margin-left-8 flex">
                  <mdicon :name="icon" size="24" />
                </span>
                <span class="font-size-14 color-complementary-300">
                  {{ caption }}
                </span>
              </li>
            </ul>
          </template>
        </DropdownModal>
      </div>
      <div
        class="position-absolute left-0 bottom-0 width-percent-100 background-surface height-72 padding-16 border-radius-bottom-8"
      >
        <div
          class="font-size-14 color-complementary-400 line-height-24 text-ellipsis flex-start-between"
        >
          <span>{{ attachmentTypeCaption }} </span>
          <span
            class="font-size-12 color-complementary-200"
            v-html="file.createdAt"
          />
        </div>
        <div
          class="font-size-12 color-complementary-200 line-height-20 text-ellipsis"
        >
          {{ caption }}
        </div>
      </div>
    </div>
    <EditAttachmentPin
      :visible="isEditAttachmentPin"
      :file="file"
      :title="$lang.editAttachment"
      @handleClose="handleCloseEditAttachmentPin"
    />
    <Modal
      :show="isViewModal"
      :title="`${fileType === 'audio' ? $lang.play : $lang.view} ${caption}`"
      :closeOnBackdrop="false"
      :hasClose="true"
      :hasFooter="false"
      contentClass="height--max-content overflow-y-auto"
      size="sm"
      @close="handleCloseViewModal"
    >
      <template #body>
        <div class="">
          <b-img-lazy
            fluid
            v-if="fileType === 'image'"
            class="object-fit--cover width-percent-100 height-percent-100 border-radius-8"
            :src="originalFile"
            :onerror="`this.src='${defaultImage}';`"
          />
          <audio v-if="fileType === 'audio'" controls controlsList="nodownload">
            <source :src="originalFile" type="audio/mp3" />
            <source :src="file.url" type="audio/mpeg" />
            <source :src="file.url" type="audio/mp3" />
            <source :src="file.url" type="audio/webm" />
            <source :src="file.url" type="audio/ogg" />
            <source :src="file.url" type="audio/3gpp" />
          </audio>
          <video
            v-if="fileType === 'video'"
            class="width-percent-100 height-percent-100"
            controls
            autoplay
            :src="originalFile"
          ></video>
        </div>
      </template>
    </Modal>
    <Modal
      :show="isCropperModal"
      :title="$lang.crop_image"
      :hasClose="false"
      size="sm"
      contentClass="height--max-content"
      @close="handleCloseCropperModal"
    >
      <template #body>
        <Cropper ref="cropComponent" :file="file" />
      </template>
    </Modal>
    <Modal
      :show="isDeleteModal"
      :title="$lang.removeAttachment"
      :closeOnBackdrop="false"
      :hasClose="false"
      :okButton="true"
      :okButtonText="$lang.remove"
      okButtonVariant="filled-error"
      size="sm"
      :ok="handleDelete"
      @close="handleCloseDeleteModal"
    >
      <template #body>
        <div class="margin-bottom-24 font-size-14 color-complementary-300">
          {{ $lang.removeSureMessage }}
        </div>
      </template>
    </Modal>
  </div>
</template>
<script>
import { DropdownModal, Modal } from "@/components/re-design";
import Cropper from "../cropper/Cropper.vue";
import EditAttachmentPin from "./EditAttachmentPin.vue";
import {
  generateConfig,
  generateError,
  generateFileType,
  generateLang,
  toastAlert,
} from "@/shared/utils";
import { pathOr, includes } from "ramda";
import { computed, inject, ref, toRefs } from "vue";
export default {
  components: { DropdownModal, EditAttachmentPin, Modal, Cropper },
  props: {
    file: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    // bind props
    const { file } = toRefs(props);

    const handleReload = inject("handleReloadList");
    const deleteMethod = inject("deleteMethod");

    const defaultImage = generateConfig(["images", "shared", "noImage"]);

    // handle view attachments
    const isViewModal = ref(false);
    const handleOpenViewModal = () => (isViewModal.value = true);
    const handleCloseViewModal = () => (isViewModal.value = false);

    // handle edit attachments
    const isEditAttachmentPin = ref(false);
    const handleOpenEditAttachmentPin = () =>
      (isEditAttachmentPin.value = true);
    const handleCloseEditAttachmentPin = () =>
      (isEditAttachmentPin.value = false);

    // handle crop modal
    const isCropperModal = ref(false);
    const handleOpenCropperModal = () => (isCropperModal.value = true);
    const handleCloseCropperModal = () => (isCropperModal.value = false);
    const createFile = (image) => {};

    // handle download attachments
    const handleDownload = () => {
      const { originalFile } = file.value;
      if (!originalFile) {
        const errorMessage = generateLang(["invalidAttachmentLink"]);
        return toastAlert(errorMessage, "error");
      }
      window.open(originalFile, "_blank");
    };

    // handle delete attachments
    const isDeleteModal = ref(false);
    const handleOpenDeleteModal = () => (isDeleteModal.value = true);
    const handleCloseDeleteModal = () => (isDeleteModal.value = false);
    const handleDelete = async () => {
      try {
        const { code } = file.value;
        const response = await deleteMethod(code);
        const { message } = response;
        toastAlert(message);
        handleCloseDeleteModal();
        handleReload();
      } catch (error) {
        generateError(error);
      }
    };

    const thumbnail = computed(() => pathOr("", ["thumbnail"], file.value));
    const originalFile = computed(() =>
      pathOr("", ["originalFile"], file.value)
    );
    const attachmentTypeCaption = computed(() =>
      pathOr("", ["attachmentTypeCaption"], file.value)
    );
    const caption = computed(() => pathOr("", ["caption"], file.value));
    const fileSuffix = computed(() => pathOr(null, ["fileSuffix"], file.value));
    const isImage = computed(
      () => generateFileType(fileSuffix.value) === "image"
    );
    const fileType = computed(() => generateFileType(fileSuffix.value));

    // generate icon of file type
    const generateFileTypeIcons = () => {
      const icons = {
        audio: "microphone-outline",
        video: "play-circle-outline",
        document: "file-document-outline",
        pdf: "file-document-outline",
        file: "file-document-outline",
      };
      return pathOr("", [fileType.value], icons);
    };
    const fileTypeIcon = computed(() => generateFileTypeIcons());

    // generate menu items
    const menuItems = computed(() => [
      {
        caption:
          fileType.value === "audio"
            ? generateLang(["play"])
            : generateLang(["show"]),
        icon: fileType.value === "audio" ? "play" : "eye-outline",
        show: includes(fileType.value, ["image", "audio", "video"]),
        loading: false,
        click: handleOpenViewModal,
      },
      {
        caption: generateLang(["edit"]),
        icon: "pencil-outline",
        show: true,
        loading: false,
        click: handleOpenEditAttachmentPin,
      },
      {
        caption: generateLang(["crop"]),
        icon: "crop",
        show: false,
        loading: false,
        click: handleOpenCropperModal,
      },
      {
        caption: generateLang(["download"]),
        icon: "tray-arrow-down",
        show: true,
        loading: false,
        click: handleDownload,
      },
      {
        caption: generateLang(["remove"]),
        icon: "trash-can-outline",
        show: true,
        loading: false,
        click: handleOpenDeleteModal,
      },
    ]);
    const filteredMenuItems = menuItems.value.filter(({ show }) => show);

    // return data to be used in template
    return {
      file,
      defaultImage,
      filteredMenuItems,
      isEditAttachmentPin,
      handleCloseEditAttachmentPin,
      isDeleteModal,
      handleCloseDeleteModal,
      handleDelete,
      isCropperModal,
      handleCloseCropperModal,
      createFile,
      isViewModal,
      handleCloseViewModal,
      thumbnail,
      originalFile,
      attachmentTypeCaption,
      caption,
      fileType,
      fileTypeIcon,
      isImage,
      menuItems,
      handleOpenViewModal,
      pathOr,
    };
  },
};
</script>
